// @flow
import React, { useState } from 'react';
import { useCombobox, useMultipleSelection } from 'downshift';
import classNames from 'classnames';
import { t } from 'javascripts/utils/withTranslation';
import Icon from 'components/_particles/icon/react/Icon';
import MediaButton from 'components/atoms/media-button/react/MediaButton';
import Checkbox from 'components/atoms/checkbox/react/Checkbox';

export type Option = {
  text?: string,
  value: string,
  selected?: boolean,
  number?: string,
};

export type Props = {
  options: Option[],
  selectedItems: Option[],
  setSelectedItems: (items: Option[]) => void,
  id?: string,
  name?: string,
  placeholder?: string,
  labelText?: string,
  large?: boolean,
};

function getFilteredItems(
  selectedItems?: Option[],
  inputValue?: string,
  options: Option[]
) {
  const lowerCasedInputValue = inputValue?.toLowerCase() || '';

  return options.filter((option) =>
    option.text?.toLowerCase().includes(lowerCasedInputValue)
  );
}

const ControlledMultiselect = ({
  options,
  selectedItems,
  setSelectedItems,
  id,
  name,
  placeholder,
  labelText,
  large,
}: Props): React$MixedElement => {
  const [inputValue, setInputValue] = useState('');
  const items = React.useMemo(
    () => getFilteredItems(selectedItems, inputValue, options),
    [selectedItems, inputValue]
  );
  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } =
    useMultipleSelection({
      selectedItems,
      onStateChange({ selectedItems: newSelectedItems, type }) {
        switch (type) {
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            setSelectedItems(newSelectedItems);
            break;
          default:
            break;
        }
      },
    });

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    clearSelection,
  } = useCombobox({
    items,
    inputValue,
    selectedItem: null,
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            ...(changes.selectedItem && { isOpen: true, highlightedIndex: 0 }),
          };
        default:
          return changes;
      }
    },
    onStateChange({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem,
    }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (newSelectedItem && !selectedItems?.includes(newSelectedItem)) {
            setSelectedItems([...(selectedItems || []), newSelectedItem]);
          } else {
            setSelectedItems(
              selectedItems?.filter((item) => item !== newSelectedItem)
            );
          }

          break;
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue);
          break;
        default:
          break;
      }
    },
  });

  const renderSelectedItem = (selectedItemForRender: Option, index: number) => (
    <span
      key={`selected-item-${selectedItemForRender.value}${index}`}
      className="multiselect__chip"
    >
      {selectedItemForRender.text}
    </span>
  );

  const handleToggle = (item: Option) => {
    if (selectedItems?.map((i) => i.value).includes(item.value)) {
      removeSelectedItem(item);
    } else {
      setSelectedItems([...(selectedItems || []), item]);
    }
  };

  return (
    <div
      className={classNames(
        'multiselect multiselect--init',
        large && 'multiselect--large'
      )}
    >
      <label className="label form-group__label" {...getLabelProps()}>
        {labelText}
      </label>
      <div className="multiselect__grid">
        {!isOpen && (
          <div
            className={classNames(
              'multiselect__chips',
              (selectedItems.length === 0 || !selectedItems) &&
                'multiselect__chips--hidden'
            )}
          >
            {selectedItems?.length < options?.length ? (
              selectedItems?.map((item, i) => renderSelectedItem(item, i))
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
        )}

        <div className="multiselect__input-wrap">
          <input
            placeholder={isOpen ? t('Auswählen') : placeholder}
            className={classNames(
              'select multiselect__select',
              large && 'select--large'
            )}
            {...getInputProps(getDropdownProps({ preventKeyAction: isOpen }))}
          />
          <button
            aria-label={t('Menü öffnen und schließen')}
            className="button button--icon multiselect__toggle"
            type="button"
            {...getToggleButtonProps()}
          >
            <Icon icon="arrow-down" />
          </button>
        </div>
      </div>
      <div
        className={classNames(
          'dropdown-menu',
          'multiselect__menu',
          !(isOpen && items.length) && 'multiselect__menu--hidden'
        )}
        {...getMenuProps()}
      >
        {isOpen && (
          <ul className="dropdown-menu__list">
            {items.map((item, index) => (
              <li
                className={classNames(
                  'dropdown-menu__item',
                  'multiselect__item',

                  selectedItem === item && 'multiselect__item--selected'
                )}
                key={`${item.value}${index}`}
                {...getItemProps({
                  item,
                  index,
                })}
              >
                <div
                  className={classNames(
                    'dropdown-menu__link',
                    highlightedIndex === index && 'current'
                  )}
                >
                  <Checkbox
                    className="dropdown-menu__text multiselect__checkbox"
                    label={`${item.text || ''}${
                      item.number ? ` (${item.number})` : ''
                    }`}
                    checked={selectedItems
                      .map((i) => i.value)
                      .includes(item.value)}
                  />
                </div>
              </li>
            ))}
            {items.length === 0 && (
              <li className="dropdown-menu__item multiselect__empty">
                {t('Nicht gefunden.')}
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ControlledMultiselect;
